// Your details on Your account: your name, and the email you sign in with. A
// new email only takes over once you tap the link we send to it, so a typo
// can't lock you out.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { ApiError, api, type Me } from "../lib/api.ts";
import { Alert, Button, Field, Section } from "./ui.tsx";

/** "14:05": when the email went, so nobody has to wonder whether it was sent. */
const clock = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

type Pending = Me["pendingEmail"];

export function YourDetails({ me }: { me: Me }) {
	const queryClient = useQueryClient();
	const [editing, setEditing] = useState<"name" | "email" | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [done, setDone] = useState<string | null>(null);
	const refresh = () => queryClient.invalidateQueries({ queryKey: ["me"] });
	const failed = (e: unknown) => setError(e instanceof ApiError ? e.message : "That didn't save. Try again.");
	const open = (which: "name" | "email") => {
		setEditing(which);
		setError(null);
		setDone(null);
	};

	const saveName = useMutation({
		mutationFn: (name: string) => api<{ name: string }>("/me", { method: "PATCH", body: { name } }),
		onSuccess: async ({ name }) => {
			// Show the new name everywhere (this page, the sidebar) before saying it's saved.
			queryClient.setQueryData<Me | null>(["me"], (old) => (old ? { ...old, user: { ...old.user, name } } : old));
			await refresh();
			setEditing(null);
			setError(null);
			setDone(`Saved. Your team now sees you as ${name}.`);
		},
		onError: failed,
	});
	const sendLink = useMutation({
		mutationFn: (email: string) => api<{ pendingEmail: Pending }>("/me/email", { body: { email } }),
		onSuccess: () => {
			setEditing(null);
			setError(null);
			void refresh();
		},
		onError: failed,
	});
	const cancel = useMutation({
		mutationFn: () => api("/me/email", { method: "DELETE" }),
		onSuccess: () => {
			setError(null);
			setDone(`Cancelled. You still sign in with ${me.user.email}.`);
			void refresh();
		},
		onError: failed,
	});

	function submit(e: FormEvent<HTMLFormElement>, which: "name" | "email") {
		e.preventDefault();
		const value = String(new FormData(e.currentTarget).get(which) ?? "").trim();
		if (which === "name") {
			if (!value) return setError("Enter your name.");
			return saveName.mutate(value);
		}
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return setError("Enter an email address, like name@agency.co.uk.");
		sendLink.mutate(value);
	}

	const pending = me.pendingEmail;
	return (
		<Section title="Your details">
			<div aria-live="polite" className="mt-3">
				{done ? <Alert tone="clear">{done}</Alert> : null}
			</div>
			<div className="divide-y divide-rule">
				<div className="pb-4">
					{editing === "name" ? (
						<form onSubmit={(e) => submit(e, "name")} noValidate>
							<Field
								label="Your name"
								name="name"
								defaultValue={me.user.name}
								autoComplete="name"
								onInput={() => setError(null)}
								maxLength={120}
								error={error}
								autoFocus
							/>
							<div className="flex flex-wrap gap-3">
								<Button type="submit" disabled={saveName.isPending}>
									{saveName.isPending ? "Saving" : "Save my name"}
								</Button>
								<Button variant="quiet" onClick={() => setEditing(null)}>
									Cancel
								</Button>
							</div>
						</form>
					) : (
						<Row label="Name" value={me.user.name} onChange={() => open("name")} what="my name" />
					)}
				</div>
				<div className="pt-4">
					{editing === "email" ? (
						<form onSubmit={(e) => submit(e, "email")} noValidate>
							<Field
								label="Your new email"
								name="email"
								type="email"
								inputMode="email"
								autoComplete="email"
								onInput={() => setError(null)}
								hint="We'll email a link to the new address. Nothing changes until you tap it."
								error={error}
								autoFocus
							/>
							<div className="flex flex-wrap gap-3">
								<Button type="submit" disabled={sendLink.isPending}>
									{sendLink.isPending ? "Sending" : "Email a link to my new address"}
								</Button>
								<Button variant="quiet" onClick={() => setEditing(null)}>
									Cancel
								</Button>
							</div>
						</form>
					) : (
						<>
							<Row
								label="Email you sign in with"
								value={me.user.email}
								onChange={() => open("email")}
								what="my sign-in email"
							/>
							{pending ? (
								<div className="mt-3">
									<Alert tone="note">
										We emailed a link to {pending.email} at {clock(pending.sentAt)}. Tap it to start signing in with
										that address. It works until {clock(pending.expiresAt)}.
									</Alert>
									{error ? <Alert>{error}</Alert> : null}
									<div className="-mt-2 -ml-2 flex flex-wrap gap-x-3">
										<Button
											variant="quiet"
											disabled={sendLink.isPending}
											onClick={() => {
												setDone(null);
												sendLink.mutate(pending.email, {
													onSuccess: () => setDone(`Sent a new link to ${pending.email}.`),
												});
											}}
										>
											Send the link again
										</Button>
										<Button variant="quiet" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
											Keep my old email
										</Button>
									</div>
								</div>
							) : null}
						</>
					)}
				</div>
			</div>
		</Section>
	);
}

function Row({ label, value, onChange, what }: { label: string; value: string; onChange: () => void; what: string }) {
	// The button says which thing it changes, so two of them never look the same.
	// Change stays on the right however long the value is; a long email wraps beside it.
	return (
		<div className="grid grid-cols-[1fr_auto] items-center gap-x-3">
			<div className="min-w-0">
				<p className="text-sm text-ink-soft">{label}</p>
				<p className="font-semibold break-all">{value}</p>
			</div>
			<Button variant="quiet" onClick={onChange} className="-mr-2">
				Change {what}
			</Button>
		</div>
	);
}
